import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** Format a fraction (e.g. 0.5) as a one-decimal percentage. Guards NaN/undefined. */
export function formatRate(rate: number | undefined): string {
  if (rate === undefined || isNaN(rate)) return '-'
  return `${(rate * 100).toFixed(1)}%`
}

export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/** The two USD shapes the app uses. Every money string in the UI comes from
 * one of the two functions below — never a private Intl.NumberFormat. */
const currencyFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const currencyFormatNoDecimals = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

/** Format a dollar amount. */
export function formatCurrency(amount: number, decimals: boolean = true): string {
  return (decimals ? currencyFormat : currencyFormatNoDecimals).format(amount)
}

/** Format a cent amount. */
export function formatCurrencyFromCents(
  cents: number,
  decimals: boolean = true,
): string {
  return formatCurrency(cents / 100, decimals)
}

/** Format a cent amount with no decimals, defaulting to $0 for a missing value. */
export function formatCents(cents: number | null | undefined): string {
  if (cents == null) return '$0'
  return formatCurrencyFromCents(cents, false)
}

const dateFormat = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
})

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '—'
  // "2026-06-12" parses as UTC midnight, so every timezone west of UTC used to
  // render the day before — an invoice due Jun 12 read "Jun 11". A date the API
  // sends without a time carries no timezone, so read it as a local calendar day.
  if (typeof date === 'string' && DATE_ONLY.test(date)) {
    const [year, month, day] = date.split('-').map(Number)
    return dateFormat.format(new Date(year, month - 1, day))
  }
  const d = new Date(date)
  if (isNaN(d.getTime())) return '—'
  return dateFormat.format(d)
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function formatDateTimeShort(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Never'
  return new Date(dateStr).toLocaleString()
}

const relativeTimeFormat = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })

const RELATIVE_TIME_UNITS: { unit: Intl.RelativeTimeFormatUnit; seconds: number }[] = [
  { unit: 'year', seconds: 31536000 },
  { unit: 'month', seconds: 2592000 },
  { unit: 'week', seconds: 604800 },
  { unit: 'day', seconds: 86400 },
  { unit: 'hour', seconds: 3600 },
  { unit: 'minute', seconds: 60 },
  { unit: 'second', seconds: 1 },
]

/** e.g. "3 hours ago", "in 5 minutes" — picks the largest sensible unit. */
export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const diffSeconds = (d.getTime() - Date.now()) / 1000

  for (const { unit, seconds } of RELATIVE_TIME_UNITS) {
    if (Math.abs(diffSeconds) >= seconds) {
      return relativeTimeFormat.format(Math.round(diffSeconds / seconds), unit)
    }
  }
  return relativeTimeFormat.format(Math.round(diffSeconds), 'second')
}

/**
 * Short-form relative time, e.g. "3h ago", "in 5m", "yesterday".
 * `opts.directional` makes future dates render as "in Xm/h/d" instead of
 * collapsing into "just now"; `opts.weekCapToDate` switches to an absolute
 * "MMM D" date (with a "yesterday" special case) once the gap reaches 7 days.
 * Reproduces the exact output of the four hand-rolled short formatters this
 * consolidates (RiskDetailPanel, TaskFeedCompactList, ActivityTimeline).
 */
export function formatRelativeTimeShort(
  date: Date | string,
  opts?: { directional?: boolean; weekCapToDate?: boolean }
): string {
  const d = typeof date === 'string' ? new Date(date) : date

  if (opts?.directional) {
    if (Number.isNaN(d.getTime())) return ''
    const diffMs = d.getTime() - Date.now()
    const absMins = Math.abs(diffMs) / 60_000
    const absHours = absMins / 60
    const absDays = absHours / 24

    if (diffMs >= 0) {
      if (absMins < 1) return 'now'
      if (absMins < 60) return `in ${Math.round(absMins)}m`
      if (absHours < 24) return `in ${Math.round(absHours)}h`
      return `in ${Math.round(absDays)}d`
    }
    if (absMins < 1) return 'just now'
    if (absMins < 60) return `${Math.round(absMins)}m ago`
    if (absHours < 24) return `${Math.round(absHours)}h ago`
    return `${Math.round(absDays)}d ago`
  }

  const diffMs = Date.now() - d.getTime()
  const diffMins = Math.floor(diffMs / 60_000)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)

  if (opts?.weekCapToDate) {
    if (diffDays === 1) return 'yesterday'
    if (diffDays < 7) return `${diffDays}d ago`
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
  return `${diffDays}d ago`
}

/** Local YYYY-MM-DD (no UTC shift). */
export function toLocalDateString(date: Date): string {
  return date.toLocaleDateString('en-CA')
}

export function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return '—'
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }
  return phone
}

/** Format phone to E.164 (+1XXXXXXXXXX) — the shape the telephony API takes. */
export function formatToE164(phone: string): string {
  if (phone.startsWith('+')) return phone.replace(/[^\d+]/g, '')
  const digits = phone.replace(/\D/g, '')
  if (digits.length === 10) return `+1${digits}`
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
  return `+${digits}`
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('')
}

/**
 * Extract a human-readable error message from an API error (Axios / DRF).
 *
 * DRF returns errors in several shapes:
 *   { detail: "..." }
 *   { email: ["..."] }
 *   { non_field_errors: ["..."] }
 *   { field: ["..."], ... }
 *
 * Falls back to `fallback` when nothing useful can be extracted.
 */
export function getApiError(error: unknown, fallback = 'Something went wrong'): string {
  if (!error) return fallback

  // Axios error — extract response body.
  // This IS the one shared extractor: it probes an unknown error's axios shape
  // so every caller can pass `unknown` instead of casting itself. The body is
  // typed as an index of unknowns rather than `any`, so each branch below has
  // to narrow before it reads a field.
  const data = (error as { response?: { data?: unknown } })?.response?.data

  if (data && typeof data === 'object') {
    // { error: "..." }
    const body = data as Record<string, unknown>
    if (typeof body.error === 'string') return body.error

    // Backend error envelope: { error: { code, message, fields: { name: ["..."] } } }
    if (body.error && typeof body.error === 'object') {
      const env = body.error as Record<string, unknown>
      const fields = env.fields as Record<string, unknown> | undefined
      if (fields && typeof fields === 'object') {
        const firstKey = Object.keys(fields).find((k) => {
          const v = fields[k]
          return Array.isArray(v) && v.length > 0 && typeof v[0] === 'string'
        })
        if (firstKey) return `${firstKey}: ${(fields[firstKey] as string[])[0]}`
      }
      if (typeof env.message === 'string' && env.message) return env.message
    }

    // { detail: "..." }
    if (typeof body.detail === 'string') return body.detail

    // { non_field_errors: ["..."] }
    if (Array.isArray(body.non_field_errors) && body.non_field_errors.length > 0) {
      return body.non_field_errors[0] as string
    }

    // First field error (array shape): { email: ["..."] }
    const firstFieldKey = Object.keys(body).find((k) => {
      const v = body[k]
      return Array.isArray(v) && v.length > 0
    })
    if (firstFieldKey) {
      const msg = (body[firstFieldKey] as unknown[])[0]
      if (typeof msg === 'string') return `${firstFieldKey}: ${msg}`
    }

    // { message: "..." } — checked before the generic string-field scan so a
    // dedicated message always wins over an arbitrary string-valued field.
    if (typeof body.message === 'string') return body.message

    // First field error (string shape): { amount_cents: "..." }. Skip the keys
    // that have dedicated branches above.
    const RESERVED = new Set(['error', 'detail', 'non_field_errors', 'message'])
    const firstStringFieldKey = Object.keys(body).find(
      (k) => !RESERVED.has(k) && typeof body[k] === 'string' && (body[k] as string).length > 0,
    )
    if (firstStringFieldKey) {
      return `${firstStringFieldKey}: ${body[firstStringFieldKey] as string}`
    }
  }

  // Plain Error
  if (error instanceof Error && error.message) return error.message

  return fallback
}

/**
 * Returns `path` only if it is a safe in-app path: a single leading slash, no
 * protocol and no protocol-relative `//` prefix. Otherwise returns `fallback`.
 * Guards against open-redirect via attacker-controlled URL params (e.g. ?returnTo=).
 */
export function safeInternalPath(path: string | null | undefined, fallback = ''): string {
  if (!path || !path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) {
    return fallback
  }
  return path
}
